<template>
  <div>
    <div class="header">
      <div class="container-fluid container-boxed">
        <div class="header-body">
          <h6 class="header-pretitle">Details</h6>
          <h1 class="header-title">Overall Performance Assessment</h1>
        </div><!-- / .header-body -->
      </div>
    </div><!-- / .header -->

    <div class="container-fluid container-boxed">
      <div class="card">
        <div class="card-body">

          <div class="row">
            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Graduate</strong>
                <br>{{ graduate.name | nullable }}
              </p>
            </div>

            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Placement Email</strong>
                <br>{{ lastPlacement ? lastPlacement.email : null | nullable }}
              </p>
            </div>

            <div class="col-6 col-lg-6 col-xl-4">
              <p>
                <strong>Agency</strong>
                <br>{{ lastPlacement.agency ? lastPlacement.agency.name : null | nullable }}
              </p>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col d-flex align-items-center">
              <p class="mb-0">
                <strong>Assigned Manager</strong>
                <br>
                <span class="mr-4">Manager Name</span>
                <template v-if="lastPlacement.manager">
                  <span class="mr-4">{{ lastPlacement.manager.name }}</span>
                  <a :href="`mailto:${lastPlacement.manager.email}`" class="mr-4 text-muted">{{ lastPlacement.manager.email }}</a>
                  <a :href="`tel:${lastPlacement.manager.phone}`" class="text-muted">{{ lastPlacement.manager.phone }}</a>
                </template>
                <span v-else class="mr-4">Manager undefined</span>
              </p>
            </div>
          </div>

        </div>
      </div><!-- /.card -->

      <div class="decorative-divider"></div>

      <div class="card">
        <div class="card-header">
          <div class="row">
            <div class="col-12 col-lg">
              <h3 class="card-header-title">Overall Performance Assessment</h3>
            </div>
            <div class="col-6 col-lg-auto text-lg-right">
              <span class="minor">
                <span class="strong">Open Date:</span>
                {{ openDate|nullable }}
              </span>
            </div>
            <div class="col-6 col-lg-auto text-lg-right">
              <span class="minor">
                <span class="strong">Due Date:</span>
                {{ dueDate|nullable }}
              </span>
            </div>
          </div><!-- /.row -->
        </div>

        <ul class="list-data no-links no-hover px-3">
          <li v-if="!isDisabled">
            <p class="strong">Description</p>
            <p>When evaluating a graduate's overall performance, consider all the information available, such as their PDP reviews and placement results.</p>
            <br>
            <p>Read comments from each manager to understand the graduate’s experience in each placement. If needed, consult a former manager for clarification on their comments or ratings.</p>
            <br>
            <p>
              Keep in mind that progress in this program can vary and is not always steady or upward. Think about any reasons for changes in the graduate's performance. If there is a valid reason, you can adjust your rating accordingly to reflect these inconsistencies.
            </p>
          </li>
          <li>
            <div class="form-group">
              <label class="control-label">Overall Performance rating </label>
              <multiselect
                v-model="rating"
                :options="ratingOptions"
                track-by="value"
                label="text"
                name="rating"
                :allow-empty="false"
                :searchable="false"
                :show-labels="false"
                v-validate="'required'"
                data-vv-as="rating"
                data-vv-validate-on="input|blur"
                :class="[errorClass('rating'), validClass('rating')]"
                :disabled="isDisabled"
              />

              <help-block field="rating"/>
            </div><!-- /.form-group -->
          </li>
          <li>
            <div class="row align-items-center">
              <div class="col mb-2">
                <p>Manager comments</p>
              </div>
            </div>
            <div class="row align-items-center">
              <div class="col-12">
                <textarea
                  v-model="manager_comments"
                  :disabled="!($userIsManager || $userIsCoordinator)"
                  class="form-control"
                  rows="4"
                  name="manager_comments"
                  data-vv-as="manager_comments"
                />

                <help-block field="manager_comments"/>
              </div>
            </div>
          </li>
        </ul><!-- /.list-data -->
        <div v-if="!isDisabled"  class="card-footer d-flex" >
          <router-link v-if="isEditing" :to="{ name: 'graduates.single', params: { id: graduate.id } }" class="btn btn-sm btn-outline-secondary">
            Cancel
          </router-link>
          <div  class="ml-lg-auto">
            <ladda-button class="btn btn-sm btn-success" :click="saveAssessment" :validate="false">
              {{ isEditing ? 'Save Assessment' : 'Create Assessment'}}
            </ladda-button>
          </div>
        </div>
    </div><!-- /.card -->

    </div><!-- /.container-fluid -->
  </div>
</template>

<script>
import { graduateComputed, graduateMethods, graduateAssessmentsComputed, graduateAssessmentsMethods } from 'vx/helpers'
import { helpers } from 'mixins/graduates'
import Multiselect from 'vue-multiselect'
import { handleServerErrors } from 'utils'

export default {
  mixins: [helpers],

  props: {
    graduateId: {
      required: false,
      type: Number,
      default: null
    }
  },

  components: {
    Multiselect
  },

  data () {
    return {
      ratingOptions: [
        {
          value: 'exceeding',
          text: 'Exceeding'
        },
        {
          value: 'achieving',
          text: 'Achieving'
        },
        {
          value: 'progressing',
          text: 'Progressing'
        },
        {
          value: 'unsatisfactory',
          text: 'Unsatisfactory'
        }
      ]
    }
  },

  computed: {
    ...graduateComputed,
    ...graduateAssessmentsComputed,

    isDisabled () {
      return this.$user.role === 'graduate'
    },
    isEditing () {
      return this.graduate.assessment && this.graduate.assessment.id
    },
    rating: {
      get () {
        return this.ratingOptions.find(option => option.value === (this.graduate.assessment ? this.graduate.assessment.rating : ''))
      },
      set (option) {
        const newAssessment = { ...this.graduate.assessment, rating: option.value }
        return this.setGraduateProperty({ property: 'assessment', value: newAssessment, force: true })
      }
    },
    manager_comments: {
      get () {
        return this.graduate.assessment ? this.graduate.assessment.manager_comments : ''
      },
      set (value) {
        const newAssessment = { ...this.graduate.assessment, manager_comments: value }
        return this.setGraduateProperty({ property: 'assessment', value: newAssessment, force: true })
      }
    },
    openDate () {
      return this.graduate.assessment.open_date ? new Date(this.graduate.assessment.open_date).toLocaleDateString('en-GB') : null
    },
    dueDate () {
      return this.graduate.assessment.open_date ? new Date(this.graduate.assessment.due_date).toLocaleDateString('en-GB') : null
    },
    lastPlacement () {
      if (!this.graduate.placements.length) return null

      return [...this.graduate.placements]
        .sort((a, b) => b.number - a.number)[0]
    }
  },

  async created () {
    await this.getGraduate(this.graduateId)
  },

  methods: {
    ...graduateMethods,
    ...graduateAssessmentsMethods,

    canEditAssessment () {
      if (!this.graduate.assessment || !this.graduate.assessment.manager_id) {
        return true
      }

      return this.graduate.assessment.manager_id === this.$user.id
    },

    async storeAssessment () {
      await this.storePerformanceAssessment({
        graduate: this.graduate,
        payload: {
          graduate_id: this.graduate.id,
          rating: this.rating ? this.rating.value : null,
          manager_comments: this.manager_comments
        }
      })

      this.$noty.success('Assessment created successfully.')
      this.$router.push({ name: 'graduates.single', params: { id: this.graduate.id } })
    },

    async updateAssessment () {
      await this.updatePerformanceAssessment({
        graduate: this.graduate,
        assessment: this.graduate.assessment,
        payload: {
          graduate_id: this.graduate.id,
          rating: this.rating ? this.rating.value : null,
          manager_comments: this.manager_comments
        }
      })

      this.$noty.success('Assessment saved successfully.')
    },

    async saveAssessment () {
      try {
        if (this.isEditing) {
          await this.updateAssessment()
        } else {
          await this.storeAssessment()
        }

        this.$validator.reset()
      } catch (error) {
        handleServerErrors(error, this.$validator)
      }
    }
  },
  beforeDestroy () {
    this.resetGraduateAssessment()
  }
}
</script>
